/**
 * Accessibility Utilities for Waltero Dashboard
 * 
 * Comprehensive utilities for ensuring WCAG 2.1 AA compliance
 * including color contrast validation, focus management,
 * screen reader support, and keyboard navigation helpers.
 */

/**
 * WCAG 2.1 AA Color Contrast Requirements
 * - Normal text: 4.5:1 contrast ratio
 * - Large text (18pt+ or 14pt+ bold): 3:1 contrast ratio
 * - Non-text elements: 3:1 contrast ratio
 */

/**
 * Convert hex color to RGB values
 * @param {string} hex - Hex color code (e.g., '#023861')
 * @returns {Object} RGB values {r, g, b}
 */
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Calculate relative luminance of a color
 * @param {Object} rgb - RGB color object {r, g, b}
 * @returns {number} Relative luminance value
 */
export const getRelativeLuminance = (rgb) => {
  const { r, g, b } = rgb;
  
  // Convert to sRGB
  const rsRGB = r / 255;
  const gsRGB = g / 255;
  const bsRGB = b / 255;
  
  // Apply gamma correction
  const rLinear = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const gLinear = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const bLinear = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);
  
  // Calculate relative luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};

/**
 * Calculate contrast ratio between two colors
 * @param {string} color1 - First color (hex)
 * @param {string} color2 - Second color (hex)
 * @returns {number} Contrast ratio
 */
export const getContrastRatio = (color1, color2) => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  
  if (!rgb1 || !rgb2) return 0;
  
  const lum1 = getRelativeLuminance(rgb1);
  const lum2 = getRelativeLuminance(rgb2);
  
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  
  return (brightest + 0.05) / (darkest + 0.05);
};

/**
 * Check if color combination meets WCAG AA standards
 * @param {string} foreground - Foreground color (hex)
 * @param {string} background - Background color (hex)
 * @param {Object} options - Validation options
 * @returns {Object} Validation result
 */
export const validateColorContrast = (foreground, background, options = {}) => {
  const {
    fontSize = 16,
    fontWeight = 'normal',
    isNonText = false
  } = options;
  
  const contrastRatio = getContrastRatio(foreground, background);
  
  // Determine requirements based on text size and type
  let requiredRatio;
  let level;
  
  if (isNonText) {
    requiredRatio = 3.0;
    level = 'AA (Non-text)';
  } else if (fontSize >= 18 || (fontSize >= 14 && fontWeight === 'bold')) {
    requiredRatio = 3.0;
    level = 'AA (Large text)';
  } else {
    requiredRatio = 4.5;
    level = 'AA (Normal text)';
  }
  
  const passes = contrastRatio >= requiredRatio;
  
  return {
    contrastRatio: Math.round(contrastRatio * 100) / 100,
    requiredRatio,
    passes,
    level,
    foreground,
    background,
    recommendation: passes ? null : generateContrastRecommendation(foreground, background, requiredRatio)
  };
};

/**
 * Generate recommendations for improving color contrast
 * @param {string} foreground - Current foreground color
 * @param {string} background - Current background color
 * @param {number} targetRatio - Target contrast ratio
 * @returns {Object} Recommendations
 */
const generateContrastRecommendation = (foreground, background, targetRatio) => {
  const fgRgb = hexToRgb(foreground);
  const bgRgb = hexToRgb(background);
  
  if (!fgRgb || !bgRgb) return null;
  
  const fgLum = getRelativeLuminance(fgRgb);
  const bgLum = getRelativeLuminance(bgRgb);
  
  const recommendations = [];
  
  if (fgLum > bgLum) {
    // Light text on dark background
    recommendations.push({
      type: 'lighten-foreground',
      description: 'Make text color lighter',
      suggestedColor: adjustLuminance(foreground, fgLum * 1.2)
    });
    recommendations.push({
      type: 'darken-background',
      description: 'Make background color darker',
      suggestedColor: adjustLuminance(background, bgLum * 0.8)
    });
  } else {
    // Dark text on light background
    recommendations.push({
      type: 'darken-foreground',
      description: 'Make text color darker',
      suggestedColor: adjustLuminance(foreground, fgLum * 0.8)
    });
    recommendations.push({
      type: 'lighten-background',
      description: 'Make background color lighter',
      suggestedColor: adjustLuminance(background, bgLum * 1.2)
    });
  }
  
  return recommendations;
};

/**
 * Adjust color luminance
 * @param {string} color - Original color (hex)
 * @param {number} factor - Adjustment factor
 * @returns {string} Adjusted color (hex)
 */
const adjustLuminance = (color, factor) => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  
  const adjust = (value) => Math.min(255, Math.max(0, Math.round(value * factor)));
  
  const newR = adjust(rgb.r);
  const newG = adjust(rgb.g);
  const newB = adjust(rgb.b);
  
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

/**
 * Validate Waltero brand colors for accessibility
 * @param {Object} theme - Waltero theme object
 * @returns {Array} Validation results
 */
export const validateWalteroColors = (theme) => {
  const walteroColors = theme.palette.waltero;
  const results = [];
  
  // Common color combinations to test
  const combinations = [
    // Primary text on backgrounds
    { fg: walteroColors.primary.coreBlue, bg: '#ffffff', name: 'Core Blue on White' },
    { fg: walteroColors.primary.coreBlue, bg: walteroColors.primary.backgroundBlue, name: 'Core Blue on Background Blue' },
    { fg: walteroColors.primary.mediumBlue, bg: '#ffffff', name: 'Medium Blue on White' },
    { fg: walteroColors.primary.mediumBlue, bg: walteroColors.secondary.backgroundApricot, name: 'Medium Blue on Background Apricot' },
    
    // Secondary text on backgrounds
    { fg: walteroColors.secondary.primaryApricot, bg: '#ffffff', name: 'Primary Apricot on White' },
    { fg: '#ffffff', bg: walteroColors.secondary.primaryApricot, name: 'White on Primary Apricot' },
    { fg: '#ffffff', bg: walteroColors.secondary.darkApricot, name: 'White on Dark Apricot' },
    
    // Status colors
    { fg: '#ffffff', bg: walteroColors.primary.lightBlue, name: 'White on Light Blue (Info)' },
    { fg: walteroColors.primary.coreBlue, bg: walteroColors.primary.paleBlue, name: 'Core Blue on Pale Blue' },
  ];
  
  combinations.forEach(({ fg, bg, name }) => {
    const result = validateColorContrast(fg, bg);
    results.push({
      name,
      ...result
    });
  });
  
  return results;
};

/**
 * Focus management utilities
 */

/**
 * Create a focus trap for modal dialogs
 * @param {HTMLElement} element - Container element
 * @returns {Object} Focus trap controls
 */
export const createFocusTrap = (element) => {
  if (!element) return null;
  
  const focusableElements = element.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  
  const handleTabKey = (e) => {
    if (e.key !== 'Tab') return;
    
    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };
  
  const activate = () => {
    element.addEventListener('keydown', handleTabKey);
    firstElement?.focus();
  };
  
  const deactivate = () => {
    element.removeEventListener('keydown', handleTabKey);
  };
  
  return { activate, deactivate, firstElement, lastElement };
};

/**
 * Manage focus restoration after modal/dialog closes
 * @param {HTMLElement} triggerElement - Element that opened the modal
 * @returns {Function} Restore function
 */
export const createFocusRestorer = (triggerElement) => {
  return () => {
    if (triggerElement && typeof triggerElement.focus === 'function') {
      // Use setTimeout to ensure the modal is fully closed
      setTimeout(() => {
        triggerElement.focus();
      }, 100);
    }
  };
};

/**
 * Screen reader utilities
 */

/**
 * Announce message to screen readers
 * @param {string} message - Message to announce
 * @param {string} priority - Announcement priority ('polite' or 'assertive')
 */
export const announceToScreenReader = (message, priority = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.setAttribute('class', 'sr-only');
  announcement.style.position = 'absolute';
  announcement.style.left = '-10000px';
  announcement.style.width = '1px';
  announcement.style.height = '1px';
  announcement.style.overflow = 'hidden';
  
  document.body.appendChild(announcement);
  
  // Set message after a brief delay to ensure screen readers detect it
  setTimeout(() => {
    announcement.textContent = message;
  }, 100);
  
  // Remove after announcement
  setTimeout(() => {
    if (announcement.parentNode) {
      announcement.parentNode.removeChild(announcement);
    }
  }, 2000);
};

/**
 * Generate accessible labels for data elements
 * @param {*} value - Data value
 * @param {Object} options - Formatting options
 * @returns {string} Accessible label
 */
export const createAccessibleLabel = (value, options = {}) => {
  const { type, unit, context, timestamp } = options;
  
  let label = '';
  
  // Handle different data types
  if (value === null || value === undefined) {
    label = 'No data available';
  } else if (typeof value === 'boolean') {
    label = value ? 'Yes' : 'No';
  } else if (typeof value === 'number') {
    if (type === 'percentage') {
      label = `${value} percent`;
    } else if (type === 'currency') {
      label = `${value} ${unit || 'dollars'}`;
    } else {
      label = `${value}${unit ? ` ${unit}` : ''}`;
    }
  } else if (value instanceof Date) {
    label = value.toLocaleString();
  } else {
    label = String(value);
  }
  
  // Add context if provided
  if (context) {
    label = `${context}: ${label}`;
  }
  
  // Add timestamp if provided
  if (timestamp) {
    label += `, last updated ${new Date(timestamp).toLocaleString()}`;
  }
  
  return label;
};

/**
 * Keyboard navigation utilities
 */

/**
 * Arrow key navigation handler for grids/tables
 * @param {Object} gridState - Current grid state {row, col, maxRow, maxCol}
 * @param {string} key - Pressed key
 * @param {Object} options - Navigation options
 * @returns {Object} New grid state
 */
export const handleArrowKeyNavigation = (gridState, key, options = {}) => {
  const { row, col, maxRow, maxCol } = gridState;
  const { wrap = false, skipDisabled = true } = options;
  
  let newRow = row;
  let newCol = col;
  
  switch (key) {
    case 'ArrowUp':
      newRow = wrap && row === 0 ? maxRow : Math.max(0, row - 1);
      break;
    case 'ArrowDown':
      newRow = wrap && row === maxRow ? 0 : Math.min(maxRow, row + 1);
      break;
    case 'ArrowLeft':
      newCol = wrap && col === 0 ? maxCol : Math.max(0, col - 1);
      break;
    case 'ArrowRight':
      newCol = wrap && col === maxCol ? 0 : Math.min(maxCol, col + 1);
      break;
    case 'Home':
      newCol = 0;
      break;
    case 'End':
      newCol = maxCol;
      break;
    case 'PageUp':
      newRow = Math.max(0, row - 10);
      break;
    case 'PageDown':
      newRow = Math.min(maxRow, row + 10);
      break;
    default:
      return gridState;
  }
  
  return { ...gridState, row: newRow, col: newCol };
};

/**
 * Generate unique IDs for accessibility
 */
let idCounter = 0;

/**
 * Generate unique ID for accessibility attributes
 * @param {string} prefix - ID prefix
 * @returns {string} Unique ID
 */
export const generateAccessibleId = (prefix = 'waltero') => {
  return `${prefix}-${++idCounter}-${Date.now()}`;
};

/**
 * ARIA attributes helpers
 */

/**
 * Create ARIA attributes for a data table
 * @param {Object} options - Table options
 * @returns {Object} ARIA attributes
 */
export const createTableAriaAttributes = (options = {}) => {
  const { label, description, sortable = false, selectable = false } = options;
  
  const attributes = {
    role: 'grid',
    'aria-label': label || 'Data table',
    'aria-readonly': !sortable && !selectable
  };
  
  if (description) {
    const descId = generateAccessibleId('table-desc');
    attributes['aria-describedby'] = descId;
  }
  
  return attributes;
};

/**
 * Create ARIA attributes for form fields
 * @param {Object} options - Field options
 * @returns {Object} ARIA attributes
 */
export const createFieldAriaAttributes = (options = {}) => {
  const { 
    label, 
    description, 
    error, 
    required = false, 
    invalid = false 
  } = options;
  
  const attributes = {
    'aria-required': required,
    'aria-invalid': invalid
  };
  
  if (label) {
    const labelId = generateAccessibleId('field-label');
    attributes['aria-labelledby'] = labelId;
  }
  
  if (description) {
    const descId = generateAccessibleId('field-desc');
    attributes['aria-describedby'] = descId;
  }
  
  if (error) {
    const errorId = generateAccessibleId('field-error');
    attributes['aria-describedby'] = attributes['aria-describedby'] 
      ? `${attributes['aria-describedby']} ${errorId}`
      : errorId;
  }
  
  return attributes;
};

/**
 * Accessibility testing utilities
 */

/**
 * Run basic accessibility audit on an element
 * @param {HTMLElement} element - Element to audit
 * @returns {Array} Array of accessibility issues
 */
export const auditAccessibility = (element) => {
  const issues = [];
  
  if (!element) {
    return [{ severity: 'error', message: 'No element provided for audit' }];
  }
  
  // Check for missing alt text on images
  const images = element.querySelectorAll('img');
  images.forEach((img, index) => {
    if (!img.alt && !img.getAttribute('aria-label')) {
      issues.push({
        severity: 'error',
        message: `Image ${index + 1} missing alt text`,
        element: img,
        recommendation: 'Add descriptive alt text or aria-label'
      });
    }
  });
  
  // Check for form fields without labels
  const inputs = element.querySelectorAll('input, select, textarea');
  inputs.forEach((input, index) => {
    const hasLabel = input.labels?.length > 0 || 
                    input.getAttribute('aria-label') || 
                    input.getAttribute('aria-labelledby');
    
    if (!hasLabel) {
      issues.push({
        severity: 'error',
        message: `Form field ${index + 1} missing label`,
        element: input,
        recommendation: 'Add label, aria-label, or aria-labelledby'
      });
    }
  });
  
  // Check for buttons without accessible names
  const buttons = element.querySelectorAll('button');
  buttons.forEach((button, index) => {
    const hasName = button.textContent?.trim() || 
                   button.getAttribute('aria-label') || 
                   button.getAttribute('aria-labelledby');
    
    if (!hasName) {
      issues.push({
        severity: 'error',
        message: `Button ${index + 1} missing accessible name`,
        element: button,
        recommendation: 'Add text content, aria-label, or aria-labelledby'
      });
    }
  });
  
  // Check for proper heading structure
  const headings = element.querySelectorAll('h1, h2, h3, h4, h5, h6');
  let lastLevel = 0;
  headings.forEach((heading, index) => {
    const level = parseInt(heading.tagName.charAt(1));
    
    if (level > lastLevel + 1) {
      issues.push({
        severity: 'warning',
        message: `Heading level ${level} skips levels after ${lastLevel}`,
        element: heading,
        recommendation: 'Use sequential heading levels'
      });
    }
    
    lastLevel = level;
  });
  
  return issues;
};

export default {
  // Color contrast
  hexToRgb,
  getRelativeLuminance,
  getContrastRatio,
  validateColorContrast,
  validateWalteroColors,
  
  // Focus management
  createFocusTrap,
  createFocusRestorer,
  
  // Screen reader support
  announceToScreenReader,
  createAccessibleLabel,
  
  // Keyboard navigation
  handleArrowKeyNavigation,
  
  // ARIA utilities
  generateAccessibleId,
  createTableAriaAttributes,
  createFieldAriaAttributes,
  
  // Testing
  auditAccessibility
};